import * as React from 'react';
import { Navbar, Nav, NavDropdown, Dropdown } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { Auth } from 'aws-amplify';
import { inject, observer } from 'mobx-react';
import { UserProfileUIStore } from './stores/user-profile-ui-store';
import { GenericModal } from './components/common/generic-modal';
import { SettingsAnnouncement } from './components/common/settings-announcement';
import { DemoVideoModal } from './components/common/demo-video-modal';

// Bump this whenever a new demo video is recorded so the auto-popup re-appears for
// everyone the next time they log in. Also update DEMO_VIDEO_YOUTUBE_ID in
// demo-video-modal.tsx to point at the new video.
const DEMO_VIDEO_VERSION = 'demo-video-2026-07';

type State = {
  currentUsername: string;
  showUpgradeModal: boolean;
  showDemoVideo: boolean;
};

interface NavigationProps {
  userProfileUIStore?: UserProfileUIStore;
}

// TODO: Figure out how we can just use the currentUser on the UserStore instead of
// what we are doing here with currentUsername
@inject('userProfileUIStore')
@observer
export class Navigation extends React.Component<NavigationProps, State> {
  state: State = {
    currentUsername: '',
    showUpgradeModal: false,
    showDemoVideo: false,
  };

  // Guards the one-time auto-popup check so it only fires once per mount, after the
  // user profile (needed for the per-user localStorage key) has loaded.
  demoAutoChecked = false;

  componentDidMount() {
    this.obtainCurrentUsername();
    // Load user profile for menu rendering
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }
    this.maybeAutoShowDemoVideo();
  }

  componentDidUpdate() {
    // The profile loads asynchronously; retry the auto-show once its id is available.
    this.maybeAutoShowDemoVideo();
  }

  demoVideoStorageKey(): string | null {
    const userProfileId = this.props.userProfileUIStore?.currentUserProfile?.id;
    return userProfileId ? `${userProfileId}-${DEMO_VIDEO_VERSION}` : null;
  }

  maybeAutoShowDemoVideo = () => {
    if (this.demoAutoChecked) {
      return;
    }
    const key = this.demoVideoStorageKey();
    if (!key) {
      // Profile not loaded yet; componentDidUpdate will retry once it is.
      return;
    }
    this.demoAutoChecked = true;
    if (localStorage.getItem(key) !== 'true') {
      this.setState({ showDemoVideo: true });
    }
  };

  openDemoVideo = () => {
    this.setState({ showDemoVideo: true });
  };

  closeDemoVideo = () => {
    const key = this.demoVideoStorageKey();
    if (key) {
      localStorage.setItem(key, 'true');
    }
    this.setState({ showDemoVideo: false });
  };

  obtainCurrentUsername = async () => {
    console.log('calling Auth.currentSession()');
    const session: any = await Auth.currentSession();
    console.log(session);
    const currentUsername = session.idToken.payload.given_name
      ? `${session.idToken.payload.given_name} ${session.idToken.payload.family_name}`
      : session.idToken.payload.email;
    this.setState({
      currentUsername,
    });
  };

  signOut = () => {
    Auth.signOut().then(() => {
      window.location.reload();
    });
  };

  handleUpgradeClick = () => {
    this.setState({ showUpgradeModal: true });
  };

  renderCustomLogoMenuItem = () => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;

    if (!userProfile) {
      return null;
    }

    return (
      <LinkContainer exact to="/settings">
        <Dropdown.Item>Settings</Dropdown.Item>
      </LinkContainer>
    );
  };

  render() {
    const { currentUsername, showUpgradeModal } = this.state;
    return (
      <div>
        <Navbar bg="dark" variant="dark" collapseOnSelect>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse>
            <Nav>
              <Nav.Item>
                <Nav.Link href="/">Home</Nav.Link>
              </Nav.Item>
              <NavDropdown title="Clients" id="clients-nav-dropdown">
                <LinkContainer exact to="/client/add">
                  <Dropdown.Item>Add Client</Dropdown.Item>
                </LinkContainer>
                <LinkContainer exact to="/client/edit/manage">
                  <Dropdown.Item>Manage Clients</Dropdown.Item>
                </LinkContainer>
              </NavDropdown>

              <NavDropdown title="Test Results" id="test-results-nav-dropdown">
                <LinkContainer exact to="/bloodwork/add">
                  <Dropdown.Item>Add Test Results</Dropdown.Item>
                </LinkContainer>
                <LinkContainer exact to="/bloodwork/analyze">
                  <Dropdown.Item>Manage Test Results</Dropdown.Item>
                </LinkContainer>
              </NavDropdown>

              <NavDropdown title="Help" id="help-nav-dropdown">
                <Dropdown.Item onClick={this.openDemoVideo}>
                  Watch Demo
                </Dropdown.Item>
                <Dropdown.Item
                  href="https://labsmarts.com/resources"
                  target="_blank"
                >
                  Resources
                </Dropdown.Item>
                <Dropdown.Item
                  href="mailto:support@labsmarts.com"
                  target="_blank"
                >
                  Support
                </Dropdown.Item>
              </NavDropdown>

              <NavDropdown title={currentUsername} id="misc-nav-dropdown">
                <Dropdown.Item
                  href="https://labsmarts.com/my-account"
                  target="_blank"
                >
                  Manage Subscription
                </Dropdown.Item>
                {this.renderCustomLogoMenuItem()}
                <Dropdown.Item onClick={this.signOut}>Signout</Dropdown.Item>
              </NavDropdown>

              <SettingsAnnouncement
                userProfileId={
                  this.props.userProfileUIStore?.currentUserProfile?.id
                }
              />
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <GenericModal
          show={showUpgradeModal}
          title="Custom Logo Feature Not Available"
          text="Custom Logo feature not enabled."
          link="https://labsmarts.com/custom-logo-upgrade"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <DemoVideoModal
          show={this.state.showDemoVideo}
          onHide={this.closeDemoVideo}
        />
      </div>
    );
  }
}
